import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, forkJoin, from, of } from 'rxjs';
import { IProduct } from '@app/models/product.interface';
import { first, map, switchMap, take, tap } from 'rxjs/operators';
import { Router } from "@angular/router"
import { environment } from '@app/../environments/environment';
import { State, select$ } from '@app/shared/helpers/store';
import { AreasService } from '@app/services/areas/areas.service';
import { CategoriesService } from '@app/services/categories/categories.service'
import { Store } from '@ngrx/store';
import { selectProductsState } from '@app/modules/products/store/selectors/products.selectors';
import * as ProductActions from '@app/modules/products/store/actions/products.actions';
import { selectCurrentItemState } from '@app/modules/organization/store/selectors/organization.selectors';
import { decrementPage, incrementPage } from '@app/modules/products/store/actions/products.actions';
import { selectPickingsState } from '@app/modules/pickings/store/selectors/pickings.selectors';
import { selectReceivingsState } from '@app/modules/receivings/store/selectors/receivings.selectors';

/** Types the API accepts for product photos (ProductImageController). */
export const PRODUCT_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
/** Splits dropped or picked files into supported photos and everything else. */
export function splitProductImageFiles(files: File[]): { valid: File[]; invalid: File[] } {
  const valid: File[] = [];
  const invalid: File[] = [];
  (files || []).forEach((f) => (PRODUCT_IMAGE_TYPES.includes(f.type) ? valid : invalid).push(f));
  return { valid, invalid };
}
/** Error message used when a file is not a supported image. */
export const PRODUCT_IMAGE_WRONG_TYPE = 'product-image-wrong-type';
/** Stay safely under the ~1 MB request limit of the web server. */
const PRODUCT_IMAGE_TARGET_BYTES = 900 * 1024;
const PRODUCT_IMAGE_MAX_EDGE = 2000;

@Injectable({
  providedIn: 'root'
})

export class ProductsService {
  public endpoint: string = `${environment.apiUrl}/products`;
  public productMasterData$: State<any[]>;
  public productMaster$: Observable<IProduct[]>;
  public totalData$: State<number>;
  public pageSizeData$: State<any>;
  public pageSize$: Observable<number>;
  public total$: Observable<any>;
  public currentPageData$: State<number>;
  public currentPage$: Observable<number>;
  public isAllowedToLeaveData$: State<boolean>;
  public isAllowedToLeave$: Observable<any>;
  public isPrdCodeValidData$: State<any>;
  public isPrdCodeValid$: Observable<any>;
  public currentPage;
  public totalPage;
  public productsCount;

  constructor(
    private _http: HttpClient,
    private router: Router,
    private _areas: AreasService,
    private _categoriesService: CategoriesService,
    private store$: Store
  ) {
    this.totalData$ = new State(0);
    this.pageSizeData$ = new State(100);
    this.total$ = select$(this.totalData$, data => data);
    this.pageSize$ = select$(this.pageSizeData$, data => data);
    this.productMasterData$ = new State([]);
    this.productMaster$ = select$(this.productMasterData$, data => data);
    this.isAllowedToLeaveData$ = new State(true);
    this.isAllowedToLeave$ = select$(this.isAllowedToLeaveData$, data => data);
    this.isPrdCodeValidData$ = new State(null);
    this.isPrdCodeValid$ = select$(this.isPrdCodeValidData$, data => data);
  }

  getProducts(sortType, direction, query?, currentPage?, prdStorage?, prdCatID?, prdIsAsset?, catalog?: boolean) {
    this.store$.select(selectProductsState).pipe(first()).subscribe(({ currentPage }) => {
      this.currentPage = currentPage < 0 ? 0 : currentPage;
    });
    const params: any = {
      pageSize: this.pageSizeData$.getValue(),
      sortType: sortType,
      direction: direction,
      search: query,
      toggleStorage: prdStorage,
      page: this.currentPage
    };
    if (prdCatID !== null && prdCatID !== undefined && prdCatID !== 0 && prdCatID !== '0') {
      params.prdCatID = prdCatID;
      // A parent category lists the products of its sub-categories too.
      params.includeSubCategories = 1;
    }
    // Type filter: 1 = assets only, 2 = products (non-assets) only. 0/empty = all.
    if (prdIsAsset !== null && prdIsAsset !== undefined && prdIsAsset !== 0 && prdIsAsset !== '0' && prdIsAsset !== '') {
      params.prdIsAsset = prdIsAsset;
    }
    // Franchise-wide catalog lookup (Browse Catalog): not limited to the
    // user's location products, always the first page.
    if (catalog) {
      params.catalog = '1';
      params.page = 0;
      return this._http.get<any>(`${this.endpoint}?page=0`, { params });
    }
    return this._http.get<any>(`${this.endpoint}?page=${this.currentPage}`, { params });
  }

  // Product tags: every tag in use across the company, most used first.
  getTags(): Observable<{ tags: { name: string, count: number }[] }> {
    return this._http.get<any>(`${this.endpoint}/tags`);
  }

  // Renames a tag on every product that has it; resolves to how many changed.
  renameTag(from: string, to: string): Observable<any> {
    return this._http.post<any>(`${this.endpoint}/tags/rename`, { from, to });
  }

  // Removes a tag from every product that has it.
  deleteTag(name: string): Observable<any> {
    return this._http.post<any>(`${this.endpoint}/tags/delete`, { name });
  }

  getProductsWithPage(sortType, direction, query?, currentPage?,pageSize?, excludeAssets?: boolean) {
    const params: any = {
      pageSize: pageSize,
      sortType: sortType,
      direction: direction,
      search: query,
      page: currentPage
    };
    if (excludeAssets) {
      params.excludeAssets = '1';
    }
    return this._http.get<any>(`${this.endpoint}?page=${currentPage}`, { params });
  }

  getProduct(id: number): Observable<any> {
    return this._http.get<any>(`${this.endpoint}/${id}`)
  }

  onSave(product: IProduct): Observable<any> {
    return this._http.post<any>(this.endpoint, product);
  }

  setPageSize(size) {
    this.pageSizeData$.next(size);
  }

  updateProduct(product) {
    return this._http.put<any>(`${this.endpoint}/${product.prdID}`, product)
  }

  // Product photo. The file is posted as multipart and the API answers with
  // the stored path plus ready-made urls; nothing else has to build them.
  uploadProductImage(prdID: number, file: File): Observable<any> {
    return from(this.prepareProductImage(file)).pipe(
      switchMap((prepared) => {
        const body = new FormData();
        body.append('image', prepared);
        return this._http.post<any>(`${this.endpoint}/${prdID}/image`, body);
      })
    );
  }

  deleteProductImage(prdID: number): Observable<any> {
    return this._http.delete<any>(`${this.endpoint}/${prdID}/image`);
  }

  /* Photo gallery (up to five per product).
     The single-photo pair above still works and operates on the gallery's
     cover slot, so older screens stay correct. Every call below answers with
     the whole gallery, cover first, so callers replace their list rather than
     patching it. */

  // In-stock serial numbers of a product (all storages of the token location),
  // one page at a time. page is 0-based, pageSize max 200.
  getSerialNumbersInStock(prdID: number, page: number = 0, pageSize: number = 200): Observable<any> {
    return this._http.get<any>(`${environment.apiUrl}/serial-numbers/in-stock`, {
      params: { prdID: String(prdID), page: String(page), pageSize: String(pageSize) }
    });
  }

  getProductImages(prdID: number): Observable<any> {
    return this._http.get<any>(`${this.endpoint}/${prdID}/images`);
  }

  addProductImage(prdID: number, file: File): Observable<any> {
    return from(this.prepareProductImage(file)).pipe(
      switchMap((prepared) => {
        const body = new FormData();
        body.append('image', prepared);
        return this._http.post<any>(`${this.endpoint}/${prdID}/images`, body);
      })
    );
  }

  /* Phone photos are several megabytes and the web server in front of the
     API refuses bodies above about 1 MB, so a large photo is scaled down and
     re-encoded as JPEG in the browser before it is sent. Small files go as
     they are. A file the browser cannot read as an image is rejected here
     with PRODUCT_IMAGE_WRONG_TYPE, so the caller can say why. */
  prepareProductImage(file: File): Promise<File> {
    if (!file || !PRODUCT_IMAGE_TYPES.includes(file.type)) {
      return Promise.reject(new Error(PRODUCT_IMAGE_WRONG_TYPE));
    }
    if (file.size <= PRODUCT_IMAGE_TARGET_BYTES || file.type === 'image/gif') {
      return Promise.resolve(file);
    }

    return new Promise<File>((resolve, reject) => {
      const url = URL.createObjectURL(file);
      const img = new Image();
      img.onload = () => {
        URL.revokeObjectURL(url);
        const scale = Math.min(1, PRODUCT_IMAGE_MAX_EDGE / Math.max(img.naturalWidth, img.naturalHeight));
        const canvas = document.createElement('canvas');
        canvas.width = Math.round(img.naturalWidth * scale);
        canvas.height = Math.round(img.naturalHeight * scale);
        const ctx = canvas.getContext('2d');
        if (!ctx) { resolve(file); return; }
        // JPEG has no alpha; paint white so transparent PNG areas stay white.
        ctx.fillStyle = '#fff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

        const encode = (quality: number) => canvas.toBlob((blob) => {
          if (!blob) { resolve(file); return; }
          if (blob.size > PRODUCT_IMAGE_TARGET_BYTES && quality > 0.5) {
            encode(quality - 0.15);
            return;
          }
          const name = file.name.replace(/\.[^.]+$/, '') + '.jpg';
          resolve(new File([blob], name, { type: 'image/jpeg' }));
        }, 'image/jpeg', quality);
        encode(0.85);
      };
      img.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error(PRODUCT_IMAGE_WRONG_TYPE));
      };
      img.src = url;
    });
  }

  deleteProductImageById(prdID: number, imageID: number): Observable<any> {
    return this._http.delete<any>(`${this.endpoint}/${prdID}/images/${imageID}`);
  }

  // Promotes a photo to cover; the API also updates prdImageFileName, which is
  // what the product lists read.
  makeProductImageCover(prdID: number, imageID: number): Observable<any> {
    return this._http.patch<any>(`${this.endpoint}/${prdID}/images/${imageID}/cover`, {});
  }

  /**
   * Photos are served straight off the web root, so the url is just the api
   * host without the /api suffix. Returns '' when the product has no photo, or
   * when the value is a legacy bare filename from the old mobile upload, which
   * lived in a folder that was never exposed.
   */
  productImageUrl(prdImageFileName: string, thumb: boolean = false): string {
    if (!prdImageFileName || !prdImageFileName.startsWith('products/')) { return ''; }
    const path = thumb
      ? prdImageFileName.replace(/(\.[^.\/]+)$/, '_thumb$1')
      : prdImageFileName;
    return `${environment.apiUrl.replace('/api', '')}/uploads/${path}`;
  }

  // Per-unit RFID tags (unique per franchise, one product per tag value).
  getRfidTags(prdID: number): Observable<any> {
    return this._http.get<any>(`${this.endpoint}/${prdID}/rfid-tags`);
  }

  addRfidTags(prdID: number, rfidValues: string[]): Observable<any> {
    return this._http.post<any>(`${this.endpoint}/${prdID}/rfid-tags`, { rfidValues });
  }

  deleteRfidTag(rfidID: number): Observable<any> {
    return this._http.delete<any>(`${this.endpoint}/rfid-tags/${rfidID}`);
  }

  // Franchise feature flags (RFID / device connections) for gating UI.
  getFranchiseFlags(): Observable<any> {
    return this._http.get<any>(`${environment.apiUrl}/franchise/flags`);
  }

  getProductsByArea(id, active, direction) {
    this.store$.select(selectProductsState).pipe(first()).subscribe(({ currentPage }) => this.currentPage = currentPage);
    return this._http.get<any>(`${this.endpoint}/area/${id}`, {
      params: {
        currentPage: this.currentPage,
        active: active,
        direction: direction
      }
    })
  }

  getProductsByStorages(id, active, direction) {
    this.store$.select(selectProductsState).pipe(first()).subscribe(({ currentPage }) => this.currentPage = currentPage);
    return this._http.get<any>(`${this.endpoint}/storages/${id}`, {
      params: {
        currentPage: this.currentPage,
        active: active,
        direction: direction
      }
    })
  }

  getProductsByCategory(id, active, direction) {
    this.store$.select(selectProductsState).pipe(first()).subscribe(({ currentPage }) => this.currentPage = currentPage);
    return this._http.get<any>(`${this.endpoint}/categories/${id}`, {
      params: {
        currentPage: this.currentPage,
        active: active,
        direction: direction
      }
    });
  }

  getProductsToggleStorage(prdStorage, active, direction) {
    this.store$.select(selectProductsState).pipe(first()).subscribe(({ currentPage }) => {
      this.currentPage = currentPage < 0 ? 0 : currentPage;
    });	
    active = active ? active : 'prdNameEn';
    direction = direction ? direction : 'ASC';

    return this._http.get<any>(`${this.endpoint}?page=${this.currentPage}`, {
      params: {
        pageSize: this.pageSizeData$.getValue(),
        sortType: active,
        direction: direction,
        search: '',
        toggleStorage: prdStorage,
        page: this.currentPage
      }
    });
  }

  getByCategory(id: number) {
    return this._http.get<any>(`${this.endpoint}/categories/${id}`);
  }

  addProductsByStorages(productIDs, storageIDs) {
    this.store$.select(selectProductsState).pipe(first()).subscribe(({ currentPage }) => this.currentPage = currentPage);
    return this._http.post<any>(`${this.endpoint}/storages/bulk_add`, {
      params: {
        productIDs: productIDs,
        storageIDs: storageIDs
      }
    })
  }

  onDelete(id: number, reason?: string) {
    this.store$.select(selectProductsState).pipe(first()).subscribe(({ currentPage,totalPage,productsCount }) => {this.currentPage = currentPage; this.totalPage = totalPage, this.productsCount = productsCount});
    if(this.productsCount == 1 && this.currentPage == this.totalPage-1){
      this.store$.dispatch(decrementPage());
    }

    return this._http.delete(`${this.endpoint}/${id}`, { body: { reason } });
  }

  // Restore a previously deleted (archived) product.
  restoreProduct(id: number): Observable<any> {
    return this._http.post<any>(`${this.endpoint}/${id}/restore`, {});
  }

  searchProducts(sort, direction, query: string): Observable<any> {
    return this._http.get<any>(`${this.endpoint}/search`, {
      params: {
        query,
        pageSize: this.pageSizeData$.getValue(),
        sortType: sort ? sort : 'prdNameEn',
        direction: direction ? direction : 'ASC',
        page: this.currentPage
      }
    });
  }

  searchProductsByCategory(query: string) {
    let id;
    this._categoriesService.currentCategory$.pipe(first()).subscribe(res => {
      id = res;
    });
    if (id !== 0) {
      this._http.get<any>(`${this.endpoint}/search/category/${id}`, {
        params: {
          query
        }
      }).pipe(first()).subscribe((data) => {
        this.totalData$.next(data.length);
      });
    } else {
      this.store$.dispatch(ProductActions.search({ query: query }))
      // this.searchProducts(query, 'prdNameEn', 'asc');
    }
  }

  importProducts(products: {}[]) {
    let headers = new HttpHeaders();
    headers.set('Content-Type', 'application/json');
    return this._http.post<any>(`${this.endpoint}/import`, {
      products
    }, { headers });
  }

  confirmImport(products: {}[]): Observable<any> {
    let headers = new HttpHeaders();
    headers.set('Content-Type', 'application/json');
    return this._http.post<any>(`${this.endpoint}/import/confirm`, {
      products
    });
  }

  confirmImportSkusBarcodes(skusBarcodes: {}[]): Observable<any> {
    let headers = new HttpHeaders();
    headers.set('Content-Type', 'application/json');
    return this._http.post<any>(`${this.endpoint}/import/confirmSkusBarcodes`, {
      skusBarcodes
    });
  }


  onSortProducts(type: string, category?) {
    return this._http.get<any>(`${this.endpoint}/sort/${type}`, {
      params: {
        category: category
      }
    }).pipe(first()).subscribe((data) => {
      this.totalData$.next(data.total);
    });
  }

  compare(a: any | string, b: number | string, isAsc: boolean) {
    return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
  }

  batchDelete(selected, reason?: string): Observable<any> {
    this.store$.select(selectProductsState).pipe(first()).subscribe(({ currentPage,totalPage,productsCount }) => {this.currentPage = currentPage; this.totalPage = totalPage, this.productsCount = productsCount});

    if(this.productsCount == selected.length && this.currentPage == this.totalPage-1){
      this.store$.dispatch(decrementPage());
    }

    return this._http.post(`${this.endpoint}/batchDelete`, { ids: selected, reason });
  }

  onGetMasterByCategory(id: number) {
    return this._http.get(`${environment.apiUrl}/products/master/all/${id}`);
  }

  onGetMasterSortByCategory(id: number) {
    return this._http.get(`${environment.apiUrl}/products/default/all/${id}`);
  }

  onRemoveFromMasterCategory(products: string[]) {
    return this._http.patch(`${environment.apiUrl}/products/master/`, {
      products: products
    })
  }

  onRemoveFromCategory(products: number[]) {
    return this._http.patch(`${environment.apiUrl}/products/default/`, {
      products: products
    });
  }

  onAddFromMasterCategory(products, category): Observable<any> {
    return this._http.patch(`${environment.apiUrl}/products/master/${category}`, {
      products: products
    });
  }

  // catID is optional to keep the categories-v2 caller working: when omitted,
  // the target category is resolved from the current-item store state.
  onAddFromCategory(products, catID?: number) {
    let id = catID;
    if (id == null) {
      this.store$.select(selectCurrentItemState).pipe(first()).subscribe(res => id = res.prdCatID);
    }
    return this._http.patch(`${environment.apiUrl}/products/default/${id}`, {
      products: products
    });
  }

  onSearchByNotInMasterCategory(query, cat) {
    return this._http.get(`${environment.apiUrl}/products/master/search/not-in/${cat}`, {
      params: {
        query: query
      }
    });
  }

  onSearchByInMasterCategory(query, cat) {
    return this._http.get(`${environment.apiUrl}/products/master/search/in/${cat}`, {
      params: {
        query: query
      }
    });
  }

  onSearchByInDefaultCategory(q, cat) {
    return this._http.get(`${environment.apiUrl}/products/default/search/in/${cat}`, {
      params: {
        query: q
      }
    });
  }

  onSearchByInArea(q, area) {
    return this._http.get(`${environment.apiUrl}/products/area/search/in/${area}`, {
      params: {
        query: q
      }
    });
  }

  onSearchByNotInDefaultCategory(q, cat) {
    return this._http.get(`${environment.apiUrl}/products/default/search/not-in/${cat}`, {
      params: {
        query: q
      }
    });
  }


  onSearchByNotInArea(q, area) {
    return this._http.get(`${environment.apiUrl}/products/area/search/not-in/${area}`, {
      params: {
        query: q
      }
    });
  }

  onSetIsAllowedToLeave(res) {
    return this.isAllowedToLeaveData$.next(res);
  }

  onGetByArea(id) {
    return this._http.get(`${environment.apiUrl}/products/area/all/${id}`);
  }

  onAddFromArea(products) {
    let id;
    this.store$.select(selectCurrentItemState).pipe(first()).subscribe(res => id = res.prdAreaID);
    return this._http.patch(`${environment.apiUrl}/products/area/all/${id}`, {
      products: products
    });
  }

  onRemoveFromArea(products) {
    let id;
    this.store$.select(selectCurrentItemState).pipe(first()).subscribe(res => id = res.prdAreaID);
    return this._http.patch(`${environment.apiUrl}/products/area/all/remove/${id}`, {
      products: products
    })
  }

  validatePrdCode(prdCode, prdID) {
    return this._http.get(`${environment.apiUrl}/products/validate-sku`, {
      params: {
        prdCode: prdCode,
        prdID: prdID
      }
    })
      .pipe(
        first(),
        map(({ data }: { data: { valid: boolean } }) => {
          return data.valid
        })
      )
  }

  validateBarcode(barcode, prdCode, prdID) {
    return this._http.get(`${environment.apiUrl}/products/validate-barcode`, {
      params: {
        barcode: barcode,
        prdCode: prdCode,
        prdID: prdID
      }
    })
      .pipe(
        first(),
        map(({ data }: { data: { valid: boolean } }) => {
          return data.valid
        })
      )
  }

  validateSerialPatterns(serialPattern, prdCode, prdID) {
    return this._http.get(`${environment.apiUrl}/products/validate-serial-patterns`, {
      params: {
        serialPatterns: serialPattern,
        prdCode: prdCode,
        prdID: prdID
      }
    })
      .pipe(
        first(),
        map(({ data }: { data: { valid: boolean } }) => {
          return data.valid
        })
      )
  }

  validateProductData(type: 'sku' | 'barcode' | 'serialPattern', sku: string,barcode:string,serialPattern:string,prdID: number) {
    let params: any = { prdID };
  
    params.validateType = type;
    params.prdCode = sku;
    params.barcodes = barcode;
    params.serialPatterns = serialPattern;
    
  
    return this._http.get<{ error: boolean; message: string, data:any }>(`${environment.apiUrl}/products/validate-barcode-sku-patterns`, { params })
      .pipe(
        first(),
        map((response) => response)
      );
  }

  // The same filters the list is showing, so the file matches the screen.
  onGenerateExcel(query?, prdStorage?, prdCatID?, prdIsAsset?): Observable<any> {
    const params: any = {};
    if (query) {
      params.search = query;
    }
    if (prdStorage !== null && prdStorage !== undefined && prdStorage !== '') {
      params.toggleStorage = prdStorage;
    }
    if (prdCatID !== null && prdCatID !== undefined && prdCatID !== 0 && prdCatID !== '0') {
      params.prdCatID = prdCatID;
    }
    // Type filter: 1 = assets only, 2 = products (non-assets) only. 0/empty = all.
    if (prdIsAsset !== null && prdIsAsset !== undefined && prdIsAsset !== 0 && prdIsAsset !== '0' && prdIsAsset !== '') {
      params.prdIsAsset = prdIsAsset;
    }
    return this._http.get(`${environment.apiUrl}/products/export`, { params });
  }

  // Resolve product info from a barcode (UPC/EAN/GTIN) to auto-fill the form.
  lookupBarcode(barcode: string, refresh = false): Observable<any> {
    return this._http.get<any>(`${this.endpoint}/lookup/${encodeURIComponent(barcode)}`, {
      params: refresh ? { refresh: '1' } : {}
    });
  }

  onSort(arr, direction) {
    let type = 'prdNameEn';

    if (arr.length <= 1) {
      return arr;
    }

    var pivot = arr[0];

    var left = [];
    var right = [];

    for (var i = 1; i < arr.length; i++) {
      if (direction == 'desc') {
        arr[i][type] < pivot[type] ? right.push(arr[i]) : left.push(arr[i]);
      } else {
        arr[i][type] < pivot[type] ? left.push(arr[i]) : right.push(arr[i]);
      }

    }

    return this.onSort(left, direction).concat(pivot, this.onSort(right, direction));
  }

  onPaginate(page) {
    this.currentPageData$.next(page);
  }

  onGeneratePrdCode() {
    return this._http.get<any>(`${this.endpoint}/generate-product-code`)
  }

  onGenerateQRCategory(id, sortType, direction) {
    const params = new HttpParams({
      fromString: `sortType=${sortType}&type=category&direction=${direction}`
    });
    return this._http.get(`${this.endpoint}/qr/${id}`, {
      params,
      responseType: 'blob'
    }).pipe(first())
      .subscribe((data: Blob) => {
        let pdf = new Blob([data], { type: 'application/pdf' });
        let url = URL.createObjectURL(pdf);
        window.open(url);
      });
  }

  onGenerateQRArea(id, sortType, direction) {
    const params = new HttpParams({
      fromString: `sortType=${sortType}&type=area&direction=${direction}`
    });
    return this._http.get(`${this.endpoint}/qr/${id}`, {
      params,
      responseType: 'blob'
    }).pipe(first())
      .subscribe((data: Blob) => {
        let pdf = new Blob([data], { type: 'application/pdf' });
        let url = URL.createObjectURL(pdf);
        window.open(url);
      });
  }

  /* Opens the QR sheet and answers how many ticked products were left out
     because they have neither a barcode nor a SKU to encode. A selection
     where none can be printed fails with a 422. */
  onGenerateQRProducts(ids): Observable<number> {
    const idsString = ids.join(',');
    const params = new HttpParams({
      fromString: `type=products&productIDs=${idsString}`
    });
    return this._http.get(`${this.endpoint}/qr/1`, {
      params,
      responseType: 'blob',
      observe: 'response'
    }).pipe(
      first(),
      map((response) => {
        const pdf = new Blob([response.body], { type: 'application/pdf' });
        window.open(URL.createObjectURL(pdf));
        return Number(response.headers.get('X-Skipped-Products')) || 0;
      })
    );
  }

  getOverridings(prdID,sortType, direction, query?, currentPage?,pageSize?) {
    this.store$.select(selectPickingsState).pipe(first()).subscribe(({ currentPage }) => this.currentPage = currentPage)
    return this._http.get<any>(`${this.endpoint}/overriding/${prdID}?page=${currentPage}`, {
      params: {
        prdID: prdID,
        pageSize: pageSize,
        sortType: sortType,
        direction: direction,
        search: query
      }
    });
  }

  getPickings(prdID,sortType, direction, query?, currentPage?,pageSize?) {
    this.store$.select(selectPickingsState).pipe(first()).subscribe(({ currentPage }) => this.currentPage = currentPage)
    return this._http.get<any>(`${this.endpoint}/pickings/${prdID}?page=${currentPage}`, {
      params: {
        prdID: prdID,
        pageSize: pageSize,
        sortType: sortType,
        direction: direction,
        search: query
      }
    });
  }

  getReceivings(prdID,sortType, direction, query?, currentPage?,pageSize?) {
    this.store$.select(selectReceivingsState).pipe(first()).subscribe(({ currentPage }) => this.currentPage = currentPage)
    return this._http.get<any>(`${this.endpoint}/receivings/${prdID}?page=${currentPage}`, {
      params: {
        prdID: prdID,
        pageSize: pageSize,
        sortType: sortType,
        direction: direction,
        search: query
      }
    });
  }

  getCountHistory(prdID: number, storageStructureID: number, pageSize: number = 5) {
    return this._http.get<any>(`${this.endpoint}/count-history/${prdID}`, {
      params: {
        storageStructureID: storageStructureID.toString(),
        pageSize: pageSize.toString()
      }
    });
  }

  // Product Alert Settings Methods
  getAlertSettings(prdID: number, locID: number) {
    return this._http.get<any>(`${this.endpoint}/${prdID}/alert-settings`, {
      params: {
        locID: locID.toString()
      }
    }).pipe(
      first(),
      map((response) => response)
    );
  }

  saveAlertSettings(prdID: number, data: { locID: number, alert_enabled: boolean, alert_threshold?: number }) {
    return this._http.post<any>(`${this.endpoint}/${prdID}/alert-settings`, data).pipe(
      first(),
      map((response) => response)
    );
  }

  deleteAlertSettings(prdID: number, locID: number) {
    return this._http.delete<any>(`${this.endpoint}/${prdID}/alert-settings`, {
      params: {
        locID: locID.toString()
      }
    }).pipe(
      first(),
      map((response) => response)
    );
  }

  saveBulkAlertSettings(data: { product_ids: number[], locID: number, alert_enabled: boolean, alert_threshold?: number }) {
    return this._http.post<any>(`${this.endpoint}/alert-settings/bulk`, data).pipe(
      first(),
      map((response) => response)
    );
  }

  cloneProduct(prdID,storageStructureID) {
    return this._http.get(`${environment.apiUrl}/products/clone/${prdID}/${storageStructureID}`)
    .pipe(
      first(),
      map((response) => response)
    );
  }

  deleteProductFromStorage(prdID,storageStructureID,reason) {
    return this._http.post(`${environment.apiUrl}/products/remove-from-storage`, {
        prdID: prdID,
        storageStructureID: storageStructureID,
        reason: reason
    })
    .pipe(
      first(),
      map((response) => response)
    );
  }

  removeProductFromLocation(prdID: number, reason?: string) {
    return this._http.post(`${environment.apiUrl}/products/remove-from-location`, {
        prdID: prdID,
        reason: reason
    })
    .pipe(
      first(),
      map((response) => response)
    );
  }

  bulkRemoveFromLocation(prdIDs: number[], reason?: string): Observable<any[]> {
    if (!prdIDs?.length) {
      return of([]);
    }
    return forkJoin(prdIDs.map(id => this.removeProductFromLocation(id, reason)));
  }

  addProductToLocation(prdID: number) {
    return this._http.post(`${environment.apiUrl}/products/add-to-location`, {
      prdID: prdID
    }).pipe(
      first(),
      map((response) => response)
    );
  }

  bulkAddToLocation(prdIDs: number[]): Observable<any[]> {
    if (!prdIDs?.length) {
      return of([]);
    }
    return forkJoin(prdIDs.map(id => this.addProductToLocation(id)));
  }

  /**
   * Validate if an asset can be converted to a normal product
   * Returns active borrows if conversion is blocked
   */
  validateAssetConversion(prdID: number, targetAssetStatus: boolean): Observable<{
    canConvert: boolean;
    reason?: string;
    activeBorrows?: any[];
    activeBorrowCount?: number;
    message?: string;
  }> {
    return this._http.post<any>(`${this.endpoint}/${prdID}/validate-asset-conversion`, {
      targetAssetStatus: targetAssetStatus ? 1 : 0
    }).pipe(
      first(),
      map((response) => response)
    );
  }


}

